<script setup lang="ts">
  import { refAutoReset, useResizeObserver } from '@vueuse/core';
  import { computed, nextTick, onMounted, ref, watch } from 'vue';
  import { useOutsidePointer } from '@/shared/composables';

  interface MorphPopoverProps {
    activeKey?: string | null;
    layerKeys: readonly string[];
  }

  interface MorphPopoverEmits {
    (e: 'outside'): void;
  }

  const props = withDefaults(defineProps<MorphPopoverProps>(), {
    activeKey: null,
  });
  const emit = defineEmits<MorphPopoverEmits>();

  // Равна длительности переезда в стилях: на это время содержимое обрезается по карточке.
  const MORPH_DURATION_MS = 500;
  const VIEWPORT_BOTTOM_GAP = 16;

  const rootRef = ref<HTMLElement | null>(null);
  const cardRef = ref<HTMLElement | null>(null);
  const indicatorRef = ref<HTMLElement | null>(null);
  const layers = ref<Record<string, HTMLElement | null>>({});
  const isOpen = ref(false);
  const isClipped = refAutoReset(false, MORPH_DURATION_MS);
  const activeLayer = computed(() => (props.activeKey ? layers.value[props.activeKey] ?? null : null));

  function getAnchor(key: string): HTMLElement | null {
    return rootRef.value?.querySelector<HTMLElement>(`[data-morph-anchor="${key}"]`) ?? null;
  }

  function getViewportWidth(): number {
    return document.documentElement.clientWidth || window.innerWidth;
  }

  function placeCard(key: string): void {
    const root = rootRef.value;
    const card = cardRef.value;
    const anchor = getAnchor(key);
    const layer = layers.value[key];
    if (!root || !card || !anchor || !layer) {
      return;
    }
    const rootRect = root.getBoundingClientRect();
    const anchorRect = anchor.getBoundingClientRect();
    layer.style.setProperty('--morph-popover-anchor-width', `${anchorRect.width}px`);
    const width = layer.offsetWidth;
    const anchorLeft = anchorRect.left - rootRect.left;
    const maxLeft = getViewportWidth() - rootRect.left - width;
    const minLeft = -rootRect.left;
    card.style.left = `${Math.max(minLeft, Math.min(anchorLeft, maxLeft))}px`;
    card.style.width = `${width}px`;
    card.style.height = `${layer.offsetHeight}px`;
  }

  function placeIndicator(key: string): void {
    const root = rootRef.value;
    const indicator = indicatorRef.value;
    const anchor = getAnchor(key);
    if (!root || !indicator || !anchor) {
      return;
    }
    const rootRect = root.getBoundingClientRect();
    const anchorRect = anchor.getBoundingClientRect();
    indicator.style.left = `${anchorRect.left - rootRect.left}px`;
    indicator.style.width = `${anchorRect.width}px`;
    indicator.style.borderRadius = window.getComputedStyle(anchor).borderRadius;
  }

  let placedKey: string | null = null;
  let placedRowGeometry = '';

  function getRowGeometry(key: string): string {
    const root = rootRef.value;
    const anchor = getAnchor(key);
    if (!root || !anchor) {
      return '';
    }
    const anchorRect = anchor.getBoundingClientRect();
    return [root.getBoundingClientRect().left, anchorRect.left, anchorRect.width, getViewportWidth()].join(',');
  }

  function layout(key: string): void {
    placeCard(key);
    placeIndicator(key);
    placedKey = key;
    placedRowGeometry = getRowGeometry(key);
  }

  function layoutWithoutTransitions(key: string): void {
    const card = cardRef.value;
    const indicator = indicatorRef.value;
    if (!card || !indicator) {
      return;
    }
    card.style.transition = 'none';
    indicator.style.transition = 'none';
    layout(key);
    // Чтение размера фиксирует новую геометрию до возврата переходов, иначе карточка приедет с прошлого места.
    card.getBoundingClientRect();
    card.style.transition = '';
    indicator.style.transition = '';
  }

  function onLayerResize(): void {
    if (!props.activeKey || props.activeKey !== placedKey) {
      return;
    }
    isClipped.value = true;
    placeCard(props.activeKey);
  }

  useResizeObserver(activeLayer, onLayerResize);

  function onRowResize(): void {
    const key = props.activeKey;
    if (!key || !isOpen.value || key !== placedKey) {
      return;
    }
    if (getRowGeometry(key) === placedRowGeometry) {
      return;
    }
    layoutWithoutTransitions(key);
  }

  const rowResizeTargets = computed(() => [
    rootRef.value?.ownerDocument.documentElement ?? null,
    props.activeKey ? getAnchor(props.activeKey) : null,
  ]);

  useResizeObserver(rowResizeTargets, onRowResize);

  function scrollCardIntoView(key: string): void {
    const root = rootRef.value;
    const card = cardRef.value;
    const layer = layers.value[key];
    if (!root || !card || !layer) {
      return;
    }
    const cardBottom = root.getBoundingClientRect().top + card.offsetTop + layer.offsetHeight;
    const overflow = Math.ceil(cardBottom + VIEWPORT_BOTTOM_GAP - window.innerHeight);
    if (overflow > 0) {
      window.scrollBy({ top: overflow, behavior: 'smooth' });
    }
  }

  watch(
    () => props.activeKey,
    async (key, previousKey) => {
      // Выпадашки полей перерисовываются после карточки: меряем слой, когда содержимое уже в нём.
      await nextTick();
      if (key !== props.activeKey) {
        return;
      }
      if (!key) {
        isOpen.value = false;
        return;
      }
      if (previousKey) {
        isClipped.value = true;
        layout(key);
      } else {
        layoutWithoutTransitions(key);
        isOpen.value = true;
      }
      scrollCardIntoView(key);
    },
  );

  const hasActiveKey = computed(() => props.activeKey !== null);

  useOutsidePointer(rootRef, { active: hasActiveKey, handler: () => emit('outside') });

  onMounted(() => {
    const root = rootRef.value;
    if (!root) {
      return;
    }
    layers.value = Object.fromEntries(
      props.layerKeys.map((key) => [key, root.querySelector<HTMLElement>(`[data-morph-layer="${key}"]`)]),
    );
  });
</script>

<template>
  <div
    ref="rootRef"
    class="morph-popover"
  >
    <div
      ref="indicatorRef"
      :class="['morph-popover__indicator', { 'morph-popover__indicator_visible': isOpen }]"
      aria-hidden="true"
    />

    <slot :layers="layers" />

    <div
      ref="cardRef"
      :class="['morph-popover__card', { 'morph-popover__card_open': isOpen, 'morph-popover__card_clipped': isClipped }]"
      data-testid="morph-popover-card"
    >
      <div
        v-for="key in layerKeys"
        :key="key"
        class="morph-popover__layer"
        :data-morph-layer="key"
      />
    </div>
  </div>
</template>

<style scoped lang="scss">
  $morph: 0.5s cubic-bezier(0.2, 0.8, 0.2, 1);

  .morph-popover {
    position: relative;

    &__indicator {
      background: var(--color-neutral-05);
      bottom: 0;
      left: 0;
      opacity: 0;
      pointer-events: none;
      position: absolute;
      top: 0;
      transition: left $morph, width $morph, border-radius $morph, opacity 0.25s ease;
      width: 0;

      &_visible {
        opacity: 1;
      }
    }

    &__card {
      background: var(--color-bg-primary);
      border-radius: var(--rounding-s);
      box-shadow: var(--shadow-m);
      left: 0;
      opacity: 0;
      pointer-events: none;
      position: absolute;
      top: calc(100% + var(--spacing-xs));
      transform: scale(0.6);
      transform-origin: 50% 0;
      transition: opacity 0.2s ease, transform 0.2s cubic-bezier(0.4, 0, 1, 1), visibility 0s linear 0.2s, left $morph,
        width $morph, height $morph;
      visibility: hidden;
      will-change: transform, opacity;
      z-index: 2;

      &_open {
        opacity: 1;
        pointer-events: auto;
        transform: none;
        transition: opacity 0.4s cubic-bezier(0.4, 0, 0.2, 1), transform $morph, visibility 0s, left $morph,
          width $morph, height $morph;
        visibility: visible;
      }

      // Только на время переезда: в покое список возраста ребёнка в «Гостях» должен выходить за карточку.
      &_clipped {
        overflow: hidden;
      }
    }

    &__layer {
      left: 0;
      position: absolute;
      top: 0;
    }

    @media (prefers-reduced-motion: reduce) {
      &__indicator,
      &__card,
      &__card_open {
        transition: none;
      }
    }
  }
</style>
